import { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TaskFormModal = ({ isOpen, onClose, task, onSave }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "To Do",
    priority: "Medium",
    category: "",
    due_date: "",
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      if (task) {
        setFormData({
          title: task.title || "",
          description: task.description || "",
          status: task.status || "To Do",
          priority: task.priority || "Medium",
          category: task.category || "",
          due_date: task.due_date || "",
        });
      } else {
        setFormData({
          title: "",
          description: "",
          status: "To Do",
          priority: "Medium",
          category: "",
          due_date: "",
        });
      }
    }
  }, [isOpen, task]);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get("/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Task title is required");
      return;
    }

    setLoading(true);
    try {
      let response;
      if (task) {
        response = await axiosInstance.put(`/tasks/${task.id}`, formData);
        toast.success("Task updated successfully");
      } else {
        response = await axiosInstance.post("/tasks", formData);
        toast.success("Task created successfully");
      }
      onSave(response.data);
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error(task ? "Failed to update task" : "Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    const categoryName = formData.category.trim();
    if (!categoryName) return;

    const exists = categories.find(
      (c) => c.name.toLowerCase() === categoryName.toLowerCase()
    );
    if (exists) return;

    try {
      const response = await axiosInstance.post("/categories", {
        name: categoryName,
      });
      setCategories([...categories, response.data]);
      toast.success("Category created");
    } catch (error) {
      console.error("Error creating category:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]" data-testid="task-form-modal">
        <DialogHeader>
          <DialogTitle data-testid="modal-title">
            {task ? "Edit Task" : "Create New Task"}
          </DialogTitle>
          <DialogDescription>
            {task ? "Update your task details below." : "Fill in the details to create a new task."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              data-testid="task-title-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter task title"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              data-testid="task-description-input"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Add more details about this task"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger data-testid="task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="To Do" data-testid="status-option-todo">
                    To Do
                  </SelectItem>
                  <SelectItem value="In Progress" data-testid="status-option-in-progress">
                    In Progress
                  </SelectItem>
                  <SelectItem value="Done" data-testid="status-option-done">
                    Done
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) =>
                  setFormData({ ...formData, priority: value })
                }
              >
                <SelectTrigger data-testid="task-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High" data-testid="priority-option-high">
                    High
                  </SelectItem>
                  <SelectItem value="Medium" data-testid="priority-option-medium">
                    Medium
                  </SelectItem>
                  <SelectItem value="Low" data-testid="priority-option-low">
                    Low
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              data-testid="task-category-input"
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              onBlur={handleCreateCategory}
              placeholder="Enter or create category"
              list="categories-list"
            />
            <datalist id="categories-list">
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name} />
              ))}
            </datalist>
          </div>

          <div className="space-y-2">
            <Label>Due Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.due_date && "text-muted-foreground"
                  )}
                  data-testid="task-due-date-button"
                >
                  {formData.due_date
                    ? format(new Date(formData.due_date), "PPP")
                    : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.due_date ? new Date(formData.due_date) : undefined}
                  onSelect={(date) =>
                    setFormData({
                      ...formData,
                      due_date: date ? date.toISOString() : "",
                    })
                  }
                  initialFocus
                  data-testid="task-due-date-calendar"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-task-button"
            >
              {loading ? "Saving..." : task ? "Update Task" : "Create Task"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TaskFormModal;
